<template>
    <CCard class="mb-0 h-100" v-show="!storePageLoading">
        <CCardHeader class="d-flex justify-content-between align-items-center">
            <h1 class="page__title text-uppercase mb-0">{{ $t(titlePage) }}</h1>
            <CButton
                v-if="loaded && hasData"
                color="secondary"
                variant="outline"
                size="sm"
                @click="exportCsv"
            >
                <CIcon
                    :content="$options.icons.cilCloudDownload"
                    class="mr-1"
                />
                {{ $t("Download CSV") }}
            </CButton>
        </CCardHeader>

        <CCardBody class="main-card-body position-relative">
            <!-- Filtros -->
            <CContainer fluid>
                <CRow class="">
                    <CCol md="8" class="offset-md-4" sm="12">
                        <CRow class="align-items-end m-0">
                            <CCol md="4" sm="12" class="selectIntegration">
                                <!-- Solo integraciones con datos en external_crm_stats -->
                                <SelectAsync
                                    :optionsValue="'uuid'"
                                    :optionsLabel="'name'"
                                    :value.sync="pluginMarketingCredentialsUuid"
                                    endpoint="/zoho/getintegrations"
                                    :fetchConfig="integrationsFetchConfig"
                                    :placeholder="$t('Select an integration')"
                                    resetOption
                                    loader
                                />
                            </CCol>
                            <CCol md="6" sm="12">
                                <!-- Rango de fechas (mismo componente que Brevo) -->
                                <FilterDate @updateData="fetchStats" />
                            </CCol>
                            <CCol md="2" sm="12" class="text-right">
                                <CButton
                                    color="primary"
                                    class="w-100"
                                    :disabled="
                                        !pluginMarketingCredentialsUuid ||
                                        loading
                                    "
                                    @click="fetchStats"
                                >
                                    <CSpinner
                                        v-if="loading"
                                        size="sm"
                                        class="mr-1"
                                    />
                                    {{ $t("View statistics") }}
                                </CButton>
                            </CCol>
                        </CRow>
                    </CCol>
                </CRow>
            </CContainer>

            <!-- Estado inicial: sin haber consultado todavía -->
            <div
                v-if="!loaded && !loading"
                class="text-center py-5 small text-muted"
            >
                {{
                    $t(
                        "Select an integration and a date range, then view statistics"
                    )
                }}
            </div>

            <!-- Cargando -->
            <div v-else-if="loading" class="text-center py-5">
                <CSpinner color="primary" />
            </div>

            <!-- Resultados (se muestran siempre que haya una carga completada) -->
            <template v-else>
                <!-- Cabecera integración + aviso si no hay registros -->
                <CContainer fluid class="my-3">
                    <p class="mb-1 small" v-if="crmIntegration">
                        <strong>{{ crmIntegration.name }}</strong>
                        <small class="text-muted"> ({{ dateLabel }})</small>
                    </p>
                    <p
                        v-if="!hasData"
                        class="text-center color-red small pt-3 pb-5"
                    >
                        {{
                            $t(
                                "No records for the selected integration and date range"
                            )
                        }}
                    </p>
                </CContainer>

                <!-- KPIs, tabla y gráfica: solo cuando hay registros -->
                <template v-if="hasData">
                    <!-- KPIs por módulo -->
                    <CContainer fluid>
                        <div class="kpi-row">
                            <div
                                v-for="kpi in kpis"
                                :key="kpi.module"
                                class="kpi-col"
                            >
                                <CCard
                                    class="h-100 mb-0 kpi-card"
                                    :style="{
                                        borderTop: '3px solid ' + kpi.color,
                                    }"
                                >
                                    <CCardBody class="py-3">
                                        <div
                                            class="text-muted text-uppercase small-xs mb-2"
                                        >
                                            <strong>
                                                {{ $t(kpi.label) }}
                                            </strong>
                                        </div>
                                        <div
                                            class="d-flex justify-content-between"
                                        >
                                            <div>
                                                <div class="h4 mb-0">
                                                    {{ kpi.created }}
                                                </div>
                                                <div class="small-xs">
                                                    {{ $t("Created") }}
                                                </div>
                                            </div>
                                            <div class="text-right">
                                                <div class="h4 mb-0">
                                                    {{ kpi.updated }}
                                                </div>
                                                <div class="small-xs">
                                                    {{ $t("Updated") }}
                                                </div>
                                            </div>
                                        </div>
                                    </CCardBody>
                                </CCard>
                            </div>
                        </div>
                    </CContainer>

                    <!-- Tabla detalle por módulo -->
                    <CContainer fluid class="mt-3">
                        <h5 class="small">
                            <strong>{{ $t("Detail by module") }}</strong>
                        </h5>
                        <table class="table table-striped">
                            <thead>
                                <tr>
                                    <th>{{ $t("Module") }}</th>
                                    <th class="text-right">
                                        {{ $t("Created") }}
                                    </th>
                                    <th class="text-right">
                                        {{ $t("Updated") }}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="kpi in kpis" :key="kpi.module">
                                    <td>{{ $t(kpi.label) }}</td>
                                    <td class="text-right">
                                        {{ kpi.created }}
                                    </td>
                                    <td class="text-right">
                                        {{ kpi.updated }}
                                    </td>
                                </tr>
                            </tbody>
                            <tfoot>
                                <tr class="font-weight-bold">
                                    <td>{{ $t("Total") }}</td>
                                    <td class="text-right">
                                        {{ totalCreated }}
                                    </td>
                                    <td class="text-right">
                                        {{ totalUpdated }}
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </CContainer>

                    <!-- Gráfica evolución por día -->
                    <CContainer fluid class="mt-3">
                        <h5 class="small">
                            <strong>{{ $t("Evolution") }}</strong>
                        </h5>
                        <CCard class="position-relative shadow">
                            <CCardBody>
                                <CChartLine
                                    :datasets="chart.datasets"
                                    :labels="chart.labels"
                                    :options="chart.options"
                                    style="height: 300px"
                                />
                            </CCardBody>
                        </CCard>
                    </CContainer>
                </template>
            </template>
        </CCardBody>
    </CCard>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { CChartLine } from "@coreui/vue-chartjs";
import { cilCloudDownload } from "@coreui/icons";
import { fetcher } from "~/utils";
import { exportCSVFile } from "~/helpers/table";
import pageMixin from "~/mixins/page.js";
import SelectAsync from "~/components/elements/SelectAsync.vue";
import FilterDate from "~/components/FilterDate.vue";

// Módulos fijos del CRM (aunque no vengan en la respuesta se muestran a 0).
const MODULES = [
    { module: "leads", label: "Leads", color: "#321fdb" },
    { module: "contacts", label: "Contacts", color: "#2eb85c" },
    { module: "opportunities", label: "Opportunities", color: "#f9b115" },
    { module: "orders", label: "Orders", color: "#e55353" },
    { module: "calls", label: "Calls", color: "#3399ff" },
];

export default {
    name: "StatisticsCrmSync",
    middleware: ["auth", "page"],
    mixins: [pageMixin],
    icons: {
        cilCloudDownload,
    },
    components: {
        SelectAsync,
        FilterDate,
        CChartLine,
    },
    data() {
        return {
            titlePage: "Zoho",
            pluginMarketingCredentialsUuid: null,
            loading: false,
            loaded: false,
        };
    },
    computed: {
        ...mapGetters({
            crmIntegration: "crmStats/integration",
            crmTotals: "crmStats/totals",
            crmByDate: "crmStats/byDate",
            darkMode: "panel/darkMode",
            filterFrom: "filter/from",
            filterTo: "filter/to",
        }),
        // baseURL para que SelectAsync pegue al backend serverless (no al API PHP)
        integrationsFetchConfig() {
            return { baseURL: process.env.VUE_APP_API_SASS };
        },
        // Fechas del store de filtros, recortadas a YYYY-MM-DD para el endpoint.
        fromDate() {
            return (this.filterFrom || "").substring(0, 10);
        },
        toDate() {
            return (this.filterTo || "").substring(0, 10);
        },
        dateLabel() {
            return `${this.fromDate} → ${this.toDate}`;
        },
        totalsByModule() {
            const map = {};
            (this.crmTotals || []).forEach((t) => {
                map[t.module] = {
                    created: Number(t.created) || 0,
                    updated: Number(t.updated) || 0,
                };
            });
            return map;
        },
        kpis() {
            return MODULES.map((m) => {
                const data = this.totalsByModule[m.module] || {
                    created: 0,
                    updated: 0,
                };
                return {
                    module: m.module,
                    label: m.label,
                    color: m.color,
                    created: data.created,
                    updated: data.updated,
                };
            });
        },
        totalCreated() {
            return this.kpis.reduce((acc, k) => acc + k.created, 0);
        },
        totalUpdated() {
            return this.kpis.reduce((acc, k) => acc + k.updated, 0);
        },
        hasData() {
            return this.totalCreated + this.totalUpdated > 0;
        },
        chart() {
            const labels = (this.crmByDate || []).map((d) => d.date);
            const created = (this.crmByDate || []).map(
                (d) => Number(d.created) || 0
            );
            const updated = (this.crmByDate || []).map(
                (d) => Number(d.updated) || 0
            );
            const gridColor = this.darkMode
                ? "rgba(255,255,255,0.1)"
                : "rgb(234, 236, 244)";
            const tickColor = this.darkMode ? "#b9b9c3" : "#6e707e";
            return {
                labels,
                datasets: [
                    {
                        label: this.$t("Created"),
                        data: created,
                        backgroundColor: "transparent",
                        borderColor: "#2eb85c",
                        pointBackgroundColor: "#2eb85c",
                    },
                    {
                        label: this.$t("Updated"),
                        data: updated,
                        backgroundColor: "transparent",
                        borderColor: "#321fdb",
                        pointBackgroundColor: "#321fdb",
                    },
                ],
                options: {
                    maintainAspectRatio: false,
                    legend: { labels: { fontColor: tickColor } },
                    scales: {
                        xAxes: [
                            {
                                gridLines: {
                                    color: gridColor,
                                    drawBorder: false,
                                },
                                ticks: { fontColor: tickColor },
                            },
                        ],
                        yAxes: [
                            {
                                gridLines: {
                                    color: gridColor,
                                    drawBorder: false,
                                },
                                ticks: {
                                    fontColor: tickColor,
                                    beginAtZero: true,
                                    precision: 0,
                                },
                            },
                        ],
                    },
                },
            };
        },
    },
    methods: {
        ...mapActions({
            setStats: "crmStats/setStats",
        }),
        // Desenvuelve la respuesta por si llega como string o envuelta en {body}.
        normalizeResponse(res) {
            let payload = res;
            if (typeof payload === "string") {
                try {
                    payload = JSON.parse(payload);
                } catch (e) {
                    payload = {};
                }
            }
            if (
                payload &&
                payload.body &&
                payload.totals === undefined &&
                payload.byDate === undefined
            ) {
                try {
                    payload = JSON.parse(payload.body);
                } catch (e) {
                    /* noop */
                }
            }
            return payload || {};
        },
        async fetchStats() {
            if (!this.pluginMarketingCredentialsUuid) return;

            this.loading = true;
            try {
                const res = await fetcher({
                    baseURL: process.env.VUE_APP_API_SASS,
                    url: "/zoho/getstadistics",
                    query: {
                        plugin_marketing_credentials_uuid:
                            this.pluginMarketingCredentialsUuid,
                        from: this.fromDate,
                        to: this.toDate,
                    },
                });

                const payload = this.normalizeResponse(res);

                await this.setStats({
                    integration: payload.integration || null,
                    totals: payload.totals || [],
                    byDate: payload.byDate || [],
                });
                this.loaded = true;
            } catch (error) {
                this.$store.dispatch("toast/addFixedToasts", {
                    header: this.$t("Error"),
                    body:
                        (error && error.message) ||
                        this.$t("Could not load statistics"),
                    color: "danger",
                });
            } finally {
                this.loading = false;
            }
        },
        exportCsv() {
            const headers = {
                module: this.$t("Module"),
                created: this.$t("Created"),
                updated: this.$t("Updated"),
            };
            const items = this.kpis.map((k) => ({
                module: this.$t(k.label),
                created: k.created,
                updated: k.updated,
            }));
            items.push({
                module: this.$t("Total"),
                created: this.totalCreated,
                updated: this.totalUpdated,
            });
            const name = this.crmIntegration?.name || "crm-sync";
            exportCSVFile({
                headers,
                items,
                fileName: `crm-sync_${name}_${this.fromDate}_${this.toDate}`,
            });
        },
    },
    mounted() {
        this.updatePageLoading(false);
    },
};
</script>

<style lang="scss" scoped>
.selectIntegration .form-group {
    margin: 0 !important;
}
.color-red {
    color: #dc3545 !important;
}
.small-xs {
    font-size: 0.75em !important;
}
.table th,
.table td {
    font-size: 0.875em;
}
.kpi-row {
    display: flex;
    flex-wrap: wrap;
    margin: 0 -0.5rem 0.5rem;
}
.kpi-col {
    flex: 1 1 0;
    min-width: 180px;
    padding: 0 0.5rem;
    margin-bottom: 1rem;
}
.kpi-card {
    transition: box-shadow 0.15s ease-in-out;
}
.kpi-card:hover {
    box-shadow: 0 0.15rem 0.5rem rgba(0, 0, 0, 0.1);
}
</style>
